import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
  HEADER_PAID_UPGRADE_EVENTS,
  HEADER_PAID_UPGRADE_LINK_LOCATION,
  HEADER_PAID_UPGRADE_UPSELL_NAME
} from 'src/app/constants/header-paid-upgrade-cta.constants';
import { ConcretePlan, PlanFactory } from 'src/app/pages/new-billing/models/plan.model';
import { PricePeriodicity } from 'src/app/pages/new-billing/types/plan.types';
import { BillingService } from 'src/app/services/billing/billing.service';
import { UpgradeService } from 'src/app/services/billing/upgrade.service';
import { GrowthBookService } from 'src/app/services/growthbook/service';
import { LocalizedPricingService } from 'src/app/services/localized-pricing/localized-pricing.service';
import { SegmentService } from 'src/app/services/segment/segment.service';
import { AmpTrackDetailValue, provideAmpTrackCtx } from 'src/app/services/tracking/amp-track';
import { CardPlan } from '../choose-plan-generic/choose-plan-generic.component';

export interface HeaderUpgradePlanDialogData {
  returnUrl: string;
}

@Component({
  selector: 'app-header-upgrade-plan-dialog',
  templateUrl: './header-upgrade-plan-dialog.component.html',
  styleUrls: ['./header-upgrade-plan-dialog.component.scss'],
  // MatDialog attaches to the CDK overlay with the root injector, so the header's
  // URL-driven AmpTrackCtx does not reach this template — provide it explicitly.
  providers: [provideAmpTrackCtx('billing')],
})
export class HeaderUpgradePlanDialogComponent implements OnInit {
  loading = true;
  loadError = false;
  continuing = false;

  currentPlan: ConcretePlan;
  upgradePlans: CardPlan[] = [];
  selectedPlan: ConcretePlan | null = null;
  payRecurrence: PricePeriodicity = 'year';

  readonly skeletonCards = [0, 1];
  readonly skeletonFeatures = [0, 1, 2, 3, 4];

  /** Plan preselected on open — lets analytics tell an active choice from accepting the default. */
  private defaultPlanName: string | null = null;
  /** Set once the user continues, so dialog close is not double-counted as a dismissal. */
  private continued = false;
  /** How the dialog was dismissed; unset only if closed without Cancel (should not happen with disableClose). */
  private dismissSource: string | null = null;

  readonly standardFeatureKeys = [
    'header.upgradePlanDialog.standardFeatures.scheduleAttendance',
    'header.upgradePlanDialog.standardFeatures.activitySummary',
    'header.upgradePlanDialog.standardFeatures.webAppUsage',
    'header.upgradePlanDialog.standardFeatures.integrations',
    'header.upgradePlanDialog.standardFeatures.payroll',
  ];

  readonly premiumFeatureKeys = [
    'header.upgradePlanDialog.premiumFeatures.videoRecording',
    'header.upgradePlanDialog.premiumFeatures.unusualActivity',
    'header.upgradePlanDialog.premiumFeatures.mouseJiggler',
    'header.upgradePlanDialog.premiumFeatures.inOfficeRemote',
    'header.upgradePlanDialog.premiumFeatures.successManager',
  ];

  constructor(
    private readonly dialogRef: MatDialogRef<HeaderUpgradePlanDialogComponent>,
    @Inject(MAT_DIALOG_DATA) readonly data: HeaderUpgradePlanDialogData,
    private readonly billingService: BillingService,
    private readonly growthBook: GrowthBookService,
    private readonly localizedPricingService: LocalizedPricingService,
    private readonly upgradeService: UpgradeService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly segment: SegmentService,
  ) {}

  ngOnInit(): void {
    // Dialog uses disableClose — closes only via Cancel / Continue (or programmatic close).
    this.dialogRef.beforeClosed().subscribe(() => {
      if (this.continued) {
        return;
      }
      this.segment.trackInAppEvents(HEADER_PAID_UPGRADE_EVENTS.modalDismissed, {
        source: this.dismissSource ?? 'unknown',
        selectedplan: this.selectedPlan?.baseName ?? null,
        defaultplan: this.defaultPlanName,
        loaderror: this.loadError,
        linklocation: HEADER_PAID_UPGRADE_LINK_LOCATION,
      });
    });

    this.billingService.getBillingData('no-cache').subscribe({
      next: (billingApiData) => {
        try {
          const enableDiscountRemoval = this.growthBook.getFeatureValue(
            'billing:discount_removal_companies',
            false,
          );
          this.currentPlan = new ConcretePlan(
            billingApiData,
            billingApiData.billingDetails.quantity,
            enableDiscountRemoval,
            this.growthBook,
            this.localizedPricingService,
          );
          // Show prices for the customer's current billing period (month → $14/$20, year → discounted monthly-eq).
          this.payRecurrence = this.currentPlan.paymentModel.paymentRecurrence === 'year' ? 'year' : 'month';

          // Standard as recommended (matches dialog mock); prices from billing API.
          const allCards = PlanFactory.createCardPlans(this.currentPlan, this.growthBook, true);
          this.upgradePlans = allCards.filter((card) => card.plan.baseName !== 'basic_plan');

          const standard = this.upgradePlans.find((c) => c.plan.baseName === 'standard_plan');
          this.selectedPlan = standard?.plan ?? this.upgradePlans[0]?.plan ?? null;
          this.defaultPlanName = this.selectedPlan?.baseName ?? null;

          this.segment.trackInAppEvents(HEADER_PAID_UPGRADE_EVENTS.modalViewed, {
            currentplan: this.currentPlan?.baseName,
            plansshown: this.upgradePlans.map((c) => c.plan.baseName).join(','),
            defaultplan: this.defaultPlanName,
            payrecurrence: this.payRecurrence,
            linklocation: HEADER_PAID_UPGRADE_LINK_LOCATION,
          });
        } catch {
          this.loadError = true;
          this.trackLoadFailure('plan-build');
        } finally {
          this.loading = false;
          this.cdr.markForCheck();
        }
      },
      error: () => {
        this.loading = false;
        this.loadError = true;
        this.trackLoadFailure('billing-fetch');
        this.cdr.markForCheck();
      },
    });
  }

  selectPlan(plan: ConcretePlan): void {
    const previous = this.selectedPlan?.baseName ?? null;
    this.selectedPlan = plan;

    if (previous !== plan.baseName) {
      this.segment.trackInAppEvents(HEADER_PAID_UPGRADE_EVENTS.planSelected, {
        selectedplan: plan.baseName,
        previousplan: previous,
        defaultplan: this.defaultPlanName,
        linklocation: HEADER_PAID_UPGRADE_LINK_LOCATION,
      });
    }

    this.cdr.markForCheck();
  }

  isSelected(plan: ConcretePlan): boolean {
    return this.selectedPlan?.baseName === plan.baseName;
  }

  featureKeysFor(plan: ConcretePlan): string[] {
    return plan.baseName === 'premium_plan' ? this.premiumFeatureKeys : this.standardFeatureKeys;
  }

  /** Canonical `plan-selector` detail value (kebab-case, registry-approved). */
  ampTrackDetailFor(plan: ConcretePlan): AmpTrackDetailValue {
    return plan.baseName === 'premium_plan' ? 'premium' : 'standard';
  }

  onCancel(): void {
    // `beforeClosed` emits the dismissal event; this only records how it happened.
    this.dismissSource = this.loadError ? 'load-error-cancel' : 'cancel-button';
    this.dialogRef.close();
  }

  onContinue(): void {
    if (!this.selectedPlan || !this.currentPlan || this.continuing) {
      return;
    }
    this.continuing = true;

    const returnUrl = this.data?.returnUrl || this.router.url;
    const selectedPlanName = this.selectedPlan.baseName;

    this.segment.trackInAppEvents(HEADER_PAID_UPGRADE_EVENTS.modalContinued, {
      selectedplan: selectedPlanName,
      defaultplan: this.defaultPlanName,
      changedselection: selectedPlanName !== this.defaultPlanName,
      currentplan: this.currentPlan.baseName,
      payrecurrence: this.payRecurrence,
      linklocation: HEADER_PAID_UPGRADE_LINK_LOCATION,
    });

    try {
      // Open Payment Summary first (sync), then close picker — no refetch / blank gap.
      this.upgradeService.confirmPlanUpgrade(this.selectedPlan, this.currentPlan, returnUrl, {
        trackingContext: {
          upsellname: HEADER_PAID_UPGRADE_UPSELL_NAME,
          linklocation: HEADER_PAID_UPGRADE_LINK_LOCATION,
        },
      });
      this.continued = true;
      this.dialogRef.close();
    } catch {
      this.continuing = false;
      this.cdr.markForCheck();
      this.segment.trackInAppEvents(HEADER_PAID_UPGRADE_EVENTS.flowFailed, {
        flow: 'choose-plan',
        stage: 'payment-summary',
        fallback: 'choose-plan-page',
        selectedplan: selectedPlanName,
        linklocation: HEADER_PAID_UPGRADE_LINK_LOCATION,
      });
      void this.router.navigate(['/new-billing'], {
        queryParams: { choosePlan: 'yes', url: returnUrl },
      });
    }
  }

  private trackLoadFailure(stage: 'billing-fetch' | 'plan-build'): void {
    this.segment.trackInAppEvents(HEADER_PAID_UPGRADE_EVENTS.flowFailed, {
      flow: 'choose-plan',
      stage,
      fallback: 'error-state',
      linklocation: HEADER_PAID_UPGRADE_LINK_LOCATION,
    });
  }
}
